import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowFatUp, MapPin, Clock, Flame, ArrowRight } from "@phosphor-icons/react";
import { fetchEscalations } from "../lib/api";
import { TypeBadge, StatusBadge } from "../components/Badges";

function fmtAbs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtRel(iso) {
  if (!iso) return "—";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diff / 60_000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.round(hrs / 24)}d ago`;
  } catch {
    return iso;
  }
}

const RANGE_OPTIONS = [
  { days: 1, label: "24h" },
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
];

export default function EscalationsPage() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState({ count: 0, items: [], window_days: 7 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchEscalations({ days })
      .then(setData)
      .catch(() => setData({ count: 0, items: [], window_days: days }))
      .finally(() => setLoading(false));
  }, [days]);

  return (
    <main className="min-h-screen bg-tac-bg text-tac-text" data-testid="escalations-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10 sm:py-14">
        <header className="mb-10 border-l-4 border-tac-primary pl-5">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-tac-primary mb-2 inline-flex items-center gap-1.5">
            <ArrowFatUp size={11} weight="fill" /> Fires we caught
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-3 leading-tight">
            Smoke check
            <br />
            <span className="text-tac-primary">→ confirmed wildfire</span>
          </h1>
          <p className="text-[15px] sm:text-[19px] text-tac-dim leading-relaxed max-w-2xl">
            Every entry below was first reported as a "smoke check" — someone
            saw a column on the ridge and called it in. Dispatch later
            confirmed it was a real fire. Firewatch logged the exact moment
            the classification changed, so anyone with a Saved Location in
            range got a push the instant ground responders said yes.
          </p>
        </header>

        {/* Range filter */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-1.5">
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.days}
                data-testid={`range-${r.days}d`}
                onClick={() => setDays(r.days)}
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                  days === r.days
                    ? "bg-tac-primary text-white border-tac-primary"
                    : "bg-tac-surface text-tac-dim border-tac-border hover:text-tac-text"
                }`}
              >
                Last {r.label}
              </button>
            ))}
          </div>
          <div className="text-[15px] font-mono-tac tabular text-tac-faint">
            {loading ? "…" : `${data.count} caught`}
          </div>
        </div>

        {/* Headline stat */}
        <section
          data-testid="caught-stat"
          className="mb-10 p-6 sm:p-8 bg-tac-surface/50 border border-tac-border rounded-sm"
        >
          <div className="flex items-end gap-5 flex-wrap">
            <div>
              <div
                className="font-display font-black tabular leading-none"
                style={{ fontSize: "84px", color: data.count > 0 ? "#F2542D" : "#666e60" }}
              >
                {data.count}
              </div>
            </div>
            <div className="flex-1 min-w-[180px]">
              <div className="text-[15px] text-tac-dim leading-snug">
                {data.count === 0 ? (
                  <>
                    No smoke checks have been escalated to wildfires in this
                    window. That's a good thing — and it means the smoke calls
                    coming in were dust, controlled burns, or fog.
                  </>
                ) : data.count === 1 ? (
                  <>
                    one smoke check confirmed as a wildfire in the last{" "}
                    {data.window_days === 1 ? "24 hours" : `${data.window_days} days`}.
                  </>
                ) : (
                  <>
                    smoke checks confirmed as wildfires in the last{" "}
                    {data.window_days === 1 ? "24 hours" : `${data.window_days} days`}.
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Timeline */}
        {loading ? (
          <div className="text-tac-faint text-[15px] text-center py-10">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="border border-tac-border rounded-sm p-10 text-center">
            <Flame size={28} weight="duotone" className="text-tac-borderBright mx-auto mb-3" />
            <div className="text-[15px] text-tac-dim">
              Quiet window. Try a longer range above.
            </div>
          </div>
        ) : (
          <ol className="relative ml-6" data-testid="escalations-list">
            <div className="absolute left-0 top-2 bottom-2 w-px bg-tac-border" aria-hidden />
            {data.items.map((inc) => (
              <li
                key={inc.id}
                data-testid={`escalation-${inc.id}`}
                className="relative pl-6 pb-6 last:pb-0"
              >
                <span
                  className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-tac-primary ring-2 ring-tac-bg"
                  aria-hidden
                />
                <Link
                  to={`/incident/${inc.id}`}
                  className="block group hover:bg-tac-surface/50 -ml-2 pl-2 py-2 rounded-sm transition-colors"
                >
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <TypeBadge type={inc.type} />
                    <StatusBadge status={inc.status_label} />
                    {inc.promoted_from_type && (
                      <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border border-tac-primary/40 bg-tac-primary/10 text-tac-primary">
                        was {inc.promoted_from_type}
                      </span>
                    )}
                  </div>
                  <div className="font-display font-bold text-base text-tac-text group-hover:text-tac-primary transition-colors leading-snug">
                    {inc.name}
                  </div>
                  <div className="text-[14px] text-tac-dim font-mono-tac tabular flex items-center gap-3 flex-wrap mt-1">
                    {inc.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={10} weight="fill" /> {inc.location}
                      </span>
                    )}
                    {inc.county && (
                      <span className="text-tac-faint">{inc.county} Co.</span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Clock size={10} weight="fill" /> {fmtRel(inc.promoted_at)} · {fmtAbs(inc.promoted_at)}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        )}

        <div className="mt-10 pt-6 border-t border-tac-border text-[14px] font-mono-tac text-tac-faint flex items-center justify-between gap-4 flex-wrap">
          <span>Detection: WildCAD-E type changes, compared each refresh cycle.</span>
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-tac-dim hover:text-tac-primary"
          >
            <ArrowRight size={11} weight="bold" className="rotate-180" /> Back to dashboard
          </Link>
        </div>
      </div>
    </main>
  );
}
